/**
 * Variables API Client
 * Handles API calls for variable registry, contacts, sender profiles, and custom fields
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5014/api/v1';

/**
 * Generic API request handler
 */
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;

  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  const response = await fetch(url, { ...defaultOptions, ...options });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Variables API
 */
export const variablesAPI = {
  /**
   * Get the variable registry (all available variables organized by category)
   * @returns {Promise<{success: boolean, data: object}>}
   */
  getRegistry: async () => {
    return apiRequest('/variables/registry');
  },

  /**
   * Resolve variables in a template with actual data
   * @param {string} template - Template content with variables
   * @param {object} options - { contactId, data }
   * @returns {Promise<{success: boolean, data: object}>}
   */
  resolve: async (template, options = {}) => {
    return apiRequest('/variables/resolve', {
      method: 'POST',
      body: JSON.stringify({ template, ...options }),
    });
  },

  /**
   * Preview variables with sample data
   * @param {string} template - Template content with variables
   * @returns {Promise<{success: boolean, data: object}>}
   */
  preview: async (template) => {
    return apiRequest('/variables/preview', {
      method: 'POST',
      body: JSON.stringify({ template }),
    });
  },
};

/**
 * Contacts API
 */
export const contactsAPI = {
  /**
   * Get all contacts with pagination
   * @param {object} params - Query parameters
   * @returns {Promise<{success: boolean, data: Array, pagination: object}>}
   */
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/contacts${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get a single contact by ID
   * @param {string} id - Contact ID
   * @returns {Promise<{success: boolean, data: object}>}
   */
  getById: async (id) => {
    return apiRequest(`/contacts/${id}`);
  },

  /**
   * Create a new contact
   * @param {object} data - Contact data
   * @returns {Promise<{success: boolean, data: object}>}
   */
  create: async (data) => {
    return apiRequest('/contacts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update a contact
   * @param {string} id - Contact ID
   * @param {object} data - Updated contact data
   * @returns {Promise<{success: boolean, data: object}>}
   */
  update: async (id, data) => {
    return apiRequest(`/contacts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a contact
   * @param {string} id - Contact ID
   * @returns {Promise<{success: boolean}>}
   */
  delete: async (id) => {
    return apiRequest(`/contacts/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Import contacts in bulk
   * @param {Array} contacts - Array of contact objects
   * @param {boolean} updateExisting - Whether to update existing contacts
   * @returns {Promise<{success: boolean, data: object}>}
   */
  import: async (contacts, updateExisting = false) => {
    return apiRequest('/contacts/import', {
      method: 'POST',
      body: JSON.stringify({ contacts, updateExisting }),
    });
  },

  /**
   * Export contacts
   * @param {object} params - Filter parameters
   * @returns {Promise<{success: boolean, data: Array}>}
   */
  export: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/contacts/export${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get unique tags
   * @returns {Promise<{success: boolean, data: Array}>}
   */
  getTags: async () => {
    return apiRequest('/contacts/tags');
  },
};

/**
 * Sender Profiles API
 */
export const senderProfilesAPI = {
  /**
   * Get all sender profiles
   * @returns {Promise<{success: boolean, data: Array}>}
   */
  getAll: async () => {
    return apiRequest('/sender-profiles');
  },

  /**
   * Get the default sender profile
   * @returns {Promise<{success: boolean, data: object}>}
   */
  getDefault: async () => {
    return apiRequest('/sender-profiles/default');
  },

  /**
   * Get a single sender profile by ID
   * @param {string} id - Profile ID
   * @returns {Promise<{success: boolean, data: object}>}
   */
  getById: async (id) => {
    return apiRequest(`/sender-profiles/${id}`);
  },

  /**
   * Create a new sender profile
   * @param {object} data - Profile data
   * @returns {Promise<{success: boolean, data: object}>}
   */
  create: async (data) => {
    return apiRequest('/sender-profiles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update a sender profile
   * @param {string} id - Profile ID
   * @param {object} data - Updated profile data
   * @returns {Promise<{success: boolean, data: object}>}
   */
  update: async (id, data) => {
    return apiRequest(`/sender-profiles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a sender profile
   * @param {string} id - Profile ID
   * @returns {Promise<{success: boolean}>}
   */
  delete: async (id) => {
    return apiRequest(`/sender-profiles/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Set a sender profile as the default
   * @param {string} id - Profile ID
   * @returns {Promise<{success: boolean, data: object}>}
   */
  setDefault: async (id) => {
    return apiRequest(`/sender-profiles/${id}/default`, {
      method: 'PUT',
    });
  },
};

/**
 * Custom Fields API
 */
export const customFieldsAPI = {
  /**
   * Get all custom fields
   * @param {object} params - Query parameters
   * @returns {Promise<{success: boolean, data: Array}>}
   */
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/custom-fields${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get a single custom field by ID
   * @param {string} id - Field ID
   * @returns {Promise<{success: boolean, data: object}>}
   */
  getById: async (id) => {
    return apiRequest(`/custom-fields/${id}`);
  },

  /**
   * Create a new custom field
   * @param {object} data - Field data
   * @returns {Promise<{success: boolean, data: object}>}
   */
  create: async (data) => {
    return apiRequest('/custom-fields', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update a custom field
   * @param {string} id - Field ID
   * @param {object} data - Updated field data
   * @returns {Promise<{success: boolean, data: object}>}
   */
  update: async (id, data) => {
    return apiRequest(`/custom-fields/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a custom field
   * @param {string} id - Field ID
   * @returns {Promise<{success: boolean}>}
   */
  delete: async (id) => {
    return apiRequest(`/custom-fields/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Reorder custom fields
   * @param {Array} fieldIds - Array of field IDs in new order
   * @returns {Promise<{success: boolean}>}
   */
  reorder: async (fieldIds) => {
    return apiRequest('/custom-fields/reorder', {
      method: 'PUT',
      body: JSON.stringify({ fieldIds }),
    });
  },
};

export default {
  variables: variablesAPI,
  contacts: contactsAPI,
  senderProfiles: senderProfilesAPI,
  customFields: customFieldsAPI,
};
